/**
 * Single day column for week view calendar.
 * Shows 24-hour time grid with events positioned absolutely by time.
 */

import { useCallback, useMemo } from 'react';

import type { ScheduleEvent } from '@/lib/base/day-schedule/event-block';
import { calculateOverlapLayout } from '@/lib/base/day-schedule/overlap-layout';
import { calculateEventPosition } from '@/lib/base/day-schedule/time-grid';
import {
  allNonOrganizerGuestsDeclined,
  DECLINED_TITLE_CLASSNAME,
} from '@/lib/crm/record/utils/meetingHelpers';
import {
  dateFromComponentsInTimezone,
  getHourInTimezone,
  getMinuteInTimezone,
} from '@/lib/scheduling/timezone';
import { useOrgTimeFormat } from '@/lib/time';
import { cx } from '@/utils/cx';

import { getEventColor } from '../utils/calendarColors';
import { calendarDayParts } from '../utils/calendarDays';
import type { EventColor } from '../utils/calendarUtils';
import { getPastEventStyles, isEventPast } from '../utils/calendarUtils';
import { formatEventTime } from '../utils/dateUtils';
import type { CalendarEvent } from '../utils/eventMapper';

interface WeekDayColumnProps {
  /** The calendar day this column shows (profile-tz midnight) */
  date: Date;
  /** Events occurring on this day (non-all-day events) */
  events: CalendarEvent[];
  /** Starting hour for the grid (0-23) */
  startHour: number;
  /** Ending hour for the grid (0-23) */
  endHour: number;
  /** Whether this column is for today */
  isToday?: boolean;
  /** Current time (for time indicator) */
  currentTime?: Date;
  /**
   * IANA timezone (the user's profile timezone) this column's hours are read
   * in — event positions, the now-line, and the instant a click-to-create
   * slot resolves to. See utils/calendarDays.ts.
   */
  timezone: string;
  /** Handler for clicking on an event */
  onEventClick?: (event: CalendarEvent) => void;
  /** ID of the currently selected event (for highlighting) */
  selectedEventId?: string;
  /**
   * Handler for clicking an empty slot in the time grid. Receives the clicked
   * instant as an ISO datetime (snapped to 15 min) and `allDay=false`. When
   * omitted, the grid background is not interactive.
   */
  onSlotSelect?: (value: string, allDay: boolean) => void;
}

/** Snap interval (minutes) for click-to-create on the time grid. */
const SLOT_SNAP_MINUTES = 15;

/**
 * Draft "ghost" reminder block on the time grid — dashed, translucent, and
 * non-interactive (clicks pass through to re-pick a slot). Extracted so the
 * dynamic position `style` prop is isolated in one clearly-named component
 * (per the no-inline-styles-in-feature-code rule) with a stable identity.
 * `top`/`height` are percentages of the grid; `minHeight` keeps a single line
 * of text + the dashed border legible on a short (e.g. 15-min) block.
 */
function DraftEventBlock({
  title,
  top,
  height,
}: {
  title: string;
  top: number;
  height: number;
}) {
  return (
    <div
      className='absolute left-0.5 right-0.5 flex items-center overflow-hidden rounded border border-dashed border-utility-brand-600 bg-utility-brand-50 pointer-events-none'
      style={{
        top: `calc(${top}% + 2px)`,
        height: `calc(${height}% - 4px)`,
        minHeight: '20px',
      }}
    >
      <span className='truncate px-1 text-xs font-medium leading-none text-utility-brand-700'>
        {title}
      </span>
    </div>
  );
}

/**
 * Convert CalendarEvent to ScheduleEvent format for overlap layout calculation.
 */
function toScheduleEvent(event: CalendarEvent): ScheduleEvent {
  return {
    id: event.id,
    title: event.title,
    start_time: event.start_time,
    end_time: event.end_time,
    location: event.location,
  };
}

/**
 * WeekDayColumn - A single day column in the week view grid.
 *
 * Features:
 * - Events positioned absolutely based on start/end times
 * - Overlap handling for concurrent events
 * - Current time indicator (red line) on today's column
 * - Event colors from Google Calendar color mapping
 */
export function WeekDayColumn({
  date,
  events,
  startHour,
  endHour,
  isToday,
  currentTime,
  timezone,
  onEventClick,
  selectedEventId,
  onSlotSelect,
}: WeekDayColumnProps) {
  const { timeFormat } = useOrgTimeFormat();

  // Convert events to ScheduleEvent format for overlap calculation. The draft
  // ghost is excluded so it never squeezes real events into narrower columns —
  // it's rendered full-width and non-interactive below.
  const scheduleEvents = useMemo(
    () => events.filter(e => !e.isDraft).map(toScheduleEvent),
    [events]
  );

  // Click on empty grid background → create a task at that snapped time.
  // Gated on `target === currentTarget` so clicks on event blocks (children)
  // don't also fire this; the time indicator is pointer-events-none.
  const handleBackgroundClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!onSlotSelect || e.target !== e.currentTarget) {
        return;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      if (rect.height === 0) {
        return;
      }
      const fraction = (e.clientY - rect.top) / rect.height;
      const totalMinutes = (endHour - startHour) * 60;
      const rawMinutes = startHour * 60 + fraction * totalMinutes;
      const snapped = Math.max(
        0,
        Math.floor(rawMinutes / SLOT_SNAP_MINUTES) * SLOT_SNAP_MINUTES
      );
      // The clicked row is a wall-clock time on the user's calendar, so the
      // instant it means is that wall clock IN THE PROFILE TIMEZONE.
      const { year, month, day } = calendarDayParts(date, timezone);
      const slot = dateFromComponentsInTimezone(
        year,
        month,
        day,
        Math.floor(snapped / 60),
        snapped % 60,
        0,
        timezone
      );
      onSlotSelect(slot.toISOString(), false);
    },
    [onSlotSelect, startHour, endHour, date, timezone]
  );

  // Calculate overlap layout for events
  const eventLayout = useMemo(
    () => calculateOverlapLayout(scheduleEvents, 3),
    [scheduleEvents]
  );

  // Create layout lookup map
  const layoutMap = useMemo(
    () => new Map(eventLayout.map(l => [l.eventId, l])),
    [eventLayout]
  );

  // Calculate current time indicator position — same timezone the events
  // above are positioned in, so the line lands between the right two events.
  const timeIndicatorPosition = useMemo(() => {
    if (!isToday || !currentTime) {
      return null;
    }

    const currentHour = getHourInTimezone(currentTime, timezone);
    const currentMinute = getMinuteInTimezone(currentTime, timezone);

    // Only show if current time is within grid bounds
    if (currentHour < startHour || currentHour >= endHour) {
      return null;
    }

    const totalMinutes = (endHour - startHour) * 60;
    const minutesFromStart = (currentHour - startHour) * 60 + currentMinute;
    return (minutesFromStart / totalMinutes) * 100;
  }, [isToday, currentTime, timezone, startHour, endHour]);

  return (
    <div
      onClick={handleBackgroundClick}
      className={cx(
        'relative h-full border-r border-secondary',
        isToday && 'bg-[var(--color-bg-brand-solid)]/5',
        onSlotSelect && 'cursor-pointer'
      )}
    >
      {/* Events */}
      {events.map(event => {
        const position = calculateEventPosition(
          event.start_time,
          event.end_time,
          startHour,
          endHour,
          date,
          timezone
        );

        // Draft "ghost" reminder: full-width, dashed, translucent, and
        // non-interactive so clicks pass through to re-pick a slot.
        if (event.isDraft) {
          return (
            <DraftEventBlock
              key={event.id}
              title={event.title}
              top={position.top}
              height={position.height}
            />
          );
        }

        const layout = layoutMap.get(event.id);
        if (!layout) {
          return null;
        }

        const { column } = layout;
        const { totalColumns } = layout;
        const widthPercent = 100 / totalColumns;
        const leftPercent = column * widthPercent;
        const color = getEventColor(event.color);
        const isPast = isEventPast(event);
        // De-emphasize meetings where every non-organizer guest declined.
        // Google still treats these as "confirmed" because the organizer
        // hasn't cancelled, so they slip past `ahoy_status != cancelled`,
        // but they can't realistically happen. Calendar should still show
        // them so the user can find and cancel; just visually de-prioritize.
        const isAllDeclined = allNonOrganizerGuestsDeclined(event.guests);
        const isSelected = event.id === selectedEventId;

        // Get styling from utility function
        const eventStyles = getPastEventStyles(
          'gray' as EventColor, // Will be replaced by proper color mapping
          isPast,
          color
        );

        // Calculate if event is short (less than 8% of grid = ~48 min for 10hr grid)
        const isShort = position.height < 8;
        // Very short events (< 4% ≈ 24 min) get smaller text to fit
        const isVeryShort = position.height < 4;

        // Check if this event has overflow (more events hidden behind it)
        const hasOverflow =
          layout.isOverflow && layout.overflowCount && layout.overflowCount > 0;

        return (
          <button
            key={event.id}
            type='button'
            onClick={() => onEventClick?.(event)}
            className={cx(
              'absolute rounded overflow-hidden cursor-pointer',
              'transition-shadow duration-100 ease-linear hover:shadow-md',
              isSelected
                ? 'ring-2 ring-[var(--color-focus-ring)]'
                : 'ring-1 ring-black/10 dark:ring-white/10',
              'text-left',
              isVeryShort ? 'text-[10px]' : 'text-xs'
            )}
            style={{
              top: `calc(${position.top}% + 2px)`,
              height: `calc(${position.height}% - 4px)`,
              minHeight: '14px',
              left: `calc(${leftPercent}% + 2px)`,
              width: `calc(${widthPercent}% - 4px)`,
              // Use inline styles from utility function (includes color-mix + hex alpha fallback)
              borderLeft: eventStyles.inlineBorderLeft,
              backgroundColor: eventStyles.inlineBackgroundColor,
              ...eventStyles.inlineStyles,
            }}
            title={`${event.title} - ${formatEventTime(event.start_time, timeFormat, timezone)}${hasOverflow ? ` (+${layout.overflowCount} more)` : ''}`}
          >
            <div
              className={cx(
                'overflow-hidden h-full',
                isVeryShort ? 'px-0.5 py-px' : 'px-1 py-0.5'
              )}
            >
              {isShort ? (
                <span
                  className={cx(
                    'font-medium truncate block',
                    isPast ? 'text-tertiary' : 'text-primary',
                    isAllDeclined && DECLINED_TITLE_CLASSNAME
                  )}
                >
                  {event.title}
                  {isPast ? ' (Past)' : ''}
                  {hasOverflow && (
                    <span className='ml-1 text-tertiary'>
                      +{layout.overflowCount}
                    </span>
                  )}
                </span>
              ) : (
                <>
                  <div
                    className={cx(
                      'font-medium truncate',
                      isPast ? 'text-tertiary' : 'text-primary',
                      isAllDeclined && DECLINED_TITLE_CLASSNAME
                    )}
                  >
                    {event.title}
                    {isPast ? ' (Past)' : ''}
                  </div>
                  <div
                    className={cx(
                      'text-[10px]',
                      isPast ? 'text-quaternary' : 'text-tertiary'
                    )}
                  >
                    {formatEventTime(event.start_time, timeFormat, timezone)}
                    {hasOverflow && (
                      <span className='ml-1 font-medium'>
                        +{layout.overflowCount} more
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </button>
        );
      })}

      {/* Current time indicator (red line) */}
      {timeIndicatorPosition !== null && (
        <div
          className='absolute left-0 right-0 flex items-center pointer-events-none z-10'
          style={{ top: `${timeIndicatorPosition}%` }}
        >
          <div className='w-2 h-2 rounded-full bg-red-500 -ml-1' />
          <div className='flex-1 h-0.5 bg-red-500' />
        </div>
      )}
    </div>
  );
}
